import { useState } from 'react';
import SafeIcon from './SafeIcon';
import CampaignWizard from './CampaignWizard';
import AddIdeaSheet from './AddIdeaSheet';
import PlanEditor from './PlanEditor';
import { TaskList } from './IdeasPage';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import { freeDay } from '../store/campaigns';
import { DEMO_TODAY } from '../store/seed';
import type { Campaign } from '../store/seed';
import {
  campaignCounts,
  campaignDates,
  campaignStatusClass,
  channelIconName,
  useDayFormat,
} from './campaignDisplay';

const goalLabel: Record<Campaign['goal'], string> = {
  product: 'Launch a product',
  season: 'Seasonal menu',
  event: 'Event or opening hours',
  awareness: 'Get known locally',
  'always-on': 'Everyday content',
};
const postStatusClass: Record<string, string> = {
  Published: 'published',
  Scheduled: 'scheduled',
  'Needs your review': 'review',
  'AMY is working': 'ready',
  Planned: 'neutral',
  'Needs your input': 'input',
  'Approved · Stopped': 'neutral',
  Rejected: 'neutral',
  'Intentionally empty': 'neutral',
  Cancelled: 'neutral',
};
const BACKLOG_LIMIT = 3;
const DAYS_PER_WEEK = 7;

type WeekWithFewerPostsThanRhythm = { from: number; to: number; have: number; need: number };

export default function CampaignDetail({ campaign }: { campaign: Campaign }) {
  const { translate, readOnlyProps, readOnly, formatCount } = useApp();
  const { route, navigate, notify, actor, openChat, role, startReview } = useUi();
  const { commands } = useDemo();
  const { posts, tasks, brand, accounts } = useWorkspaceData(route.workspaceId);
  const formatDay = useDayFormat();
  const [editing, setEditing] = useState(false);
  const [ending, setEnding] = useState(false);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [stopScheduled, setStopScheduled] = useState(false);
  const [answer, setAnswer] = useState('');
  const [showDeclined, setShowDeclined] = useState(false);
  const counts = campaignCounts(posts, campaign);
  const campaignPosts = [...counts.own].sort(
    (first, second) => first.day - second.day || first.time.localeCompare(second.time)
  );
  const myTasks = tasks.filter(
    (task) => task.campaignId === campaign.id && task.status !== 'Cancelled'
  );
  const thinking = myTasks.some(
    (task) =>
      (task.kind === 'ideas' || task.kind === 'plan') &&
      (task.status === 'Queued' || task.status === 'Working')
  );
  const waiting = myTasks.find((task) => task.status === 'Waiting');
  const suggested = campaign.ideas.filter((idea) => idea.status === 'Suggested');
  const declined = campaign.ideas.filter((idea) => idea.status === 'Declined');
  const inCalendar = campaign.ideas.filter((idea) => idea.status === 'In calendar');
  const closed = campaign.status === 'Finished';
  const canManage = !readOnly && role !== 'admin';
  const cancellable = campaignPosts.filter(
    (post) =>
      post.day > DEMO_TODAY &&
      ['Planned', 'AMY is working', 'Needs your review', 'Needs your input'].includes(post.status)
  ).length;
  const scheduledLater = campaignPosts.filter(
    (post) => post.day > DEMO_TODAY && post.status === 'Scheduled'
  ).length;
  const nextFreeDayPerChannel = campaign.channels.map((channel) => ({
    channel,
    next: freeDay(
      posts,
      campaign.workspaceId,
      channel,
      campaign.start ?? DEMO_TODAY + 1,
      campaign.end ?? DEMO_TODAY + 14
    ),
  }));
  const missingMaterial = campaignPosts.filter((post) => post.status === 'Needs your input');
  const backlog = counts.review >= BACKLOG_LIMIT;
  const samples = campaignPosts.filter(
    (post) => post.history[0]?.text === 'Sample post from the campaign plan'
  );
  const sampling = !!campaign.plan && campaign.samplesApproved === false;
  const planLeft = campaign.plan?.filter((slot) => !slot.postId).length ?? 0;
  const accountNames = (campaign.accounts ?? [])
    .map((id) => accounts.find((account) => account.id === id))
    .filter(Boolean);
  const gaps: WeekWithFewerPostsThanRhythm[] = [];
  if (campaign.start && campaign.end && !closed) {
    for (
      let weekStart = Math.max(campaign.start, DEMO_TODAY + 1);
      weekStart <= campaign.end;
      weekStart += DAYS_PER_WEEK
    ) {
      const to = Math.min(weekStart + DAYS_PER_WEEK - 1, campaign.end);
      const have = campaignPosts.filter(
        (post) =>
          post.day >= weekStart &&
          post.day <= to &&
          !['Rejected', 'Intentionally empty', 'Cancelled'].includes(post.status)
      ).length;
      const need = Math.floor((campaign.perWeek * (to - weekStart + 1)) / DAYS_PER_WEEK);
      if (need > 0 && have < need) gaps.push({ from: weekStart, to, have, need });
    }
  }

  function accept(ideaId: string) {
    const acceptResult = commands.acceptIdea(campaign.id, ideaId, actor);
    if (acceptResult.ok)
      notify(
        translate('Added to the calendar on {day}. AMY is drafting it now.', {
          day: formatDay(acceptResult.day),
        })
      );
    else if (acceptResult.reason === 'account')
      notify(
        translate('{channel} needs reconnecting before AMY can plan posts for it.', {
          channel: acceptResult.channel,
        }),
        'warn'
      );
    else if (acceptResult.reason === 'channel')
      notify(
        translate(
          '{channel} is no longer one of this campaign’s channels. Add it back in Edit, or decline the idea.',
          { channel: acceptResult.channel }
        ),
        'warn'
      );
    else if (acceptResult.reason === 'paused')
      notify(
        translate('Content creation is paused for this campaign. Resume it to add ideas.'),
        'warn'
      );
    else
      notify(
        translate(
          'No free {channel} slot between {from} and {to}. Free a slot or change the campaign dates.',
          {
            channel: acceptResult.channel,
            from: formatDay(campaign.start ?? 19),
            to: formatDay(campaign.end ?? 30),
          }
        ),
        'warn'
      );
  }
  function pause(which: 'creation' | 'publishing', shouldPause: boolean) {
    const pauseResult = commands.setCampaignPause(campaign.id, which, shouldPause, actor);
    if (which === 'creation')
      notify(
        translate(
          shouldPause
            ? 'Content creation paused. Scheduled posts still publish.'
            : 'Content creation resumed.'
        )
      );
    else notify(publishingPauseMessage(shouldPause, pauseResult));
  }

  function publishingPauseMessage(
    shouldPause: boolean,
    pauseResult: ReturnType<typeof commands.setCampaignPause>
  ) {
    if (shouldPause)
      return translate('Publishing paused. {n} scheduled posts are held.', { n: pauseResult.held });
    if (pauseResult.blocked)
      return translate(
        'Publishing resumed. {n} posts went back to the schedule; {b} stay held because their slot is taken.',
        { n: pauseResult.held, b: pauseResult.blocked }
      );
    return translate('Publishing resumed. {n} posts went back to the schedule.', {
      n: pauseResult.held,
    });
  }
  function runBatchNow() {
    const draftedPostCount = commands.runBatch(campaign.id, actor);
    if (draftedPostCount)
      notify(translate('AMY is drafting {n} posts now.', { n: draftedPostCount }));
    else notify(translate('No free slots for a new batch.'), 'warn');
  }
  const reviewCampaignPosts = () =>
    startReview(
      undefined,
      campaignPosts.map((post) => post.id)
    );
  const unfilledPlanSlotsClashingWithCalendar = (campaign.plan ?? []).filter(
    (slot) =>
      !slot.postId &&
      posts.some(
        (post) =>
          post.channel === slot.channel &&
          post.day === slot.day &&
          !['Approved · Stopped', 'Rejected', 'Intentionally empty', 'Cancelled'].includes(
            post.status
          )
      )
  );

  return (
    <div className="content-stack campaign-detail">
      <button className="link-button back-link" onClick={() => navigate({ page: 'ideas' })}>
        <SafeIcon name="FiArrowLeft" size={14} /> {translate('All campaigns')}
      </button>
      <div className="page-title-row">
        <div style={{ minWidth: 0 }}>
          <h1>{campaign.name}</h1>
          <div className="subtitle campaign-sub">
            <span className={`status ${campaignStatusClass[campaign.status]}`}>
              {translate(campaign.status)}
            </span>
            {campaign.creationPaused && (
              <span className="status neutral">
                <SafeIcon name="FiPauseCircle" size={12} />
                {translate('Creation paused')}
              </span>
            )}
            {campaign.publishingPaused && (
              <span className="status input">
                <SafeIcon name="FiPauseCircle" size={12} />
                {translate('Publishing paused')}
              </span>
            )}
            <span>{campaignDates(campaign, formatDay, translate)}</span>
            <span>
              {campaign.channels.map((channel) => (
                <span key={channel} className="channel-tag">
                  <SafeIcon name={channelIconName[channel] as 'FiInstagram'} size={13} />
                  {channel}
                </span>
              ))}
            </span>
            <span className="muted">
              {translate(goalLabel[campaign.goal])} ·{' '}
              {translate('{n} posts a week', { n: campaign.perWeek })} ·{' '}
              {translate('Owner: {name}', { name: campaign.owner ?? campaign.createdBy })}
            </span>
          </div>
        </div>
        <div className="button-row">
          {!closed && (
            <button className="button" onClick={() => setIdeaOpen(true)} {...readOnlyProps}>
              <SafeIcon name="FiZap" />
              {translate('Add an idea')}
            </button>
          )}
          <button
            className="button"
            onClick={() => openChat(undefined, commands.ensureCampaignConversation(campaign.id))}
          >
            <SafeIcon name="FiMessageCircle" />
            {translate('Campaign chat')}
          </button>
          {!closed && (
            <button className="button" onClick={() => setEditing(true)} {...readOnlyProps}>
              <SafeIcon name="FiEdit2" />
              {translate('Edit')}
            </button>
          )}
        </div>
      </div>

      {(campaign.status === 'Needs your input' || waiting) &&
        (campaign.question || waiting?.question) && (
          <div className="panel campaign-input">
            <h2>
              <SafeIcon name="FiHelpCircle" size={18} /> {translate('Waiting for your answer')}
            </h2>
            <p>{translate((waiting?.question ?? campaign.question)!)}</p>
            <textarea
              className="text-field"
              rows={3}
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              aria-label={translate('Your answer')}
              placeholder={translate(
                'For example: closed 24–26 December, open 10–14 on 31 December.'
              )}
              {...readOnlyProps}
            />
            <div className="button-row" style={{ marginTop: 8 }}>
              <button
                className="button primary"
                disabled={readOnly || answer.trim().length < 3}
                onClick={() => {
                  commands.answerCampaignQuestion(campaign.id, answer.trim(), actor);
                  setAnswer('');
                  notify(translate('Thanks. AMY continues with your answer.'));
                }}
              >
                <SafeIcon name="FiSend" />
                {translate('Send answer')}
              </button>
              {waiting && (
                <button
                  className="button"
                  onClick={() => {
                    commands.handToAgency(waiting.id, actor);
                    notify(translate('Handed to Hakusana. They reply in the conversation.'));
                  }}
                  {...readOnlyProps}
                >
                  <SafeIcon name="FiUsers" />
                  {translate('Hand to agency')}
                </button>
              )}
            </div>
          </div>
        )}
      {closed && (
        <div className="notice-banner">
          <SafeIcon name="FiFlag" />
          <span>
            {translate('This campaign has ended. Published posts and their results stay here.')}
          </span>
        </div>
      )}
      {campaign.plan && (
        <div className="panel campaign-plan">
          <div className="panel-header">
            <h2>
              <SafeIcon name="FiList" size={18} /> {translate('Plan')}
            </h2>
            <span className="muted">
              {translate('{n} posts · {left} not drafted yet', {
                n: campaign.plan.length,
                left: planLeft,
              })}
            </span>
          </div>
          {campaign.status === 'Plan ready' && (
            <div className="notice-banner">
              <SafeIcon name="FiInfo" />
              <span>
                {translate(
                  'Review the plan. Activating drafts the first two posts as samples; once you approve them, AMY drafts the rest in batches of {n}.',
                  { n: campaign.batchSize ?? 3 }
                )}
              </span>
              <button
                className="button small primary"
                onClick={() => {
                  const samplePostCount = commands.activateCampaign(campaign.id, actor);
                  notify(
                    translate('Campaign active. AMY is drafting {n} sample posts.', {
                      n: samplePostCount,
                    })
                  );
                }}
                disabled={!canManage || campaign.creationPaused}
              >
                <SafeIcon name="FiPlay" size={14} />
                {translate('Activate campaign')}
              </button>
            </div>
          )}
          {sampling && campaign.status === 'Active' && (
            <div className="notice-banner">
              <SafeIcon name="FiClock" />
              <span>
                {translate(
                  'Samples: approve the {n} sample posts and AMY drafts the next batch automatically.',
                  { n: samples.length }
                )}
              </span>
            </div>
          )}
          {unfilledPlanSlotsClashingWithCalendar.length > 0 && (
            <div className="notice-banner warn">
              <SafeIcon name="FiAlertTriangle" />
              <span>
                {translate(
                  '{n} planned posts clash with the calendar. Edit their day before activating.',
                  { n: unfilledPlanSlotsClashingWithCalendar.length }
                )}
              </span>
            </div>
          )}
          <PlanEditor
            campaign={campaign}
            plan={campaign.plan}
            editable={canManage && !closed}
            onChange={(plan) => commands.updatePlan(campaign.id, plan)}
          />
        </div>
      )}
      <div className="panel campaign-progress">
        <div className="campaign-stats">
          <div>
            <strong>{counts.published}</strong>
            <span>{translate('Published')}</span>
          </div>
          <div>
            <strong>{counts.scheduled}</strong>
            <span>{translate('Scheduled')}</span>
          </div>
          <div>
            <strong>{counts.review}</strong>
            <span>{translate('Needs your review')}</span>
          </div>
          <div>
            <strong>{counts.working}</strong>
            <span>{translate('AMY is working')}</span>
          </div>
        </div>
        {!closed && campaign.status !== 'Draft' && campaign.status !== 'Plan ready' && (
          <div className="batch-row">
            {backlog ? (
              <div className="notice-banner warn">
                <SafeIcon name="FiPauseCircle" />
                <span>
                  {translate(
                    'AMY is waiting: {n} posts are waiting for your review. The next batch starts when fewer than {limit} are waiting.',
                    { n: counts.review, limit: BACKLOG_LIMIT }
                  )}
                </span>
                <button className="button small primary" onClick={reviewCampaignPosts}>
                  {translate('Review {n}', { n: counts.review })}
                </button>
              </div>
            ) : campaign.creationPaused ? (
              <p className="muted">
                {translate('No automatic batches while content creation is paused.')}
              </p>
            ) : sampling ? null : (
              <p className="muted">
                <SafeIcon name="FiRepeat" size={13} />{' '}
                {translate('Next automatic batch: {day} · {n} posts', {
                  day: formatDay(Math.max(campaign.nextBatch ?? DEMO_TODAY + 2, DEMO_TODAY + 1)),
                  n: Math.min(
                    campaign.batchSize ?? 3,
                    campaign.plan ? planLeft : (campaign.batchSize ?? 3)
                  ),
                })}
                {canManage && (!campaign.plan || planLeft > 0) && (
                  <button className="link-button" onClick={runBatchNow}>
                    {translate('Run it now (demo)')}
                  </button>
                )}
              </p>
            )}
            {!backlog && counts.review > 0 && (
              <button className="button primary small" onClick={reviewCampaignPosts}>
                {translate('Review {n}', { n: counts.review })}
              </button>
            )}
          </div>
        )}
        {!closed && (
          <p className="muted small-print">
            {translate('Next free slots')}:{' '}
            {nextFreeDayPerChannel
              .map(
                ({ channel, next }) =>
                  `${channel} ${next ? formatDay(next) : translate('none in these dates')}`
              )
              .join(' · ')}
          </p>
        )}
      </div>

      {(missingMaterial.length > 0 || gaps.length > 0) && (
        <div className="panel campaign-gaps">
          <h2>
            <SafeIcon name="FiAlertCircle" size={18} /> {translate('Gaps')}
          </h2>
          {missingMaterial.map((post) => (
            <button
              key={post.id}
              className="checklist-item list-button"
              onClick={() => navigate({ page: 'post', id: post.id })}
            >
              <SafeIcon name="FiImage" />
              <span style={{ flex: 1 }}>
                <strong>{translate('Material missing')}</strong> · {post.title} · {post.channel}
              </span>
              <span className="muted">{formatDay(post.day)}</span>
            </button>
          ))}
          {gaps.map((gap) => (
            <div key={gap.from} className="checklist-item">
              <SafeIcon name="FiCalendar" />
              <span style={{ flex: 1 }}>
                {translate('{from} – {to}: {have} of {need} posts planned', {
                  from: formatDay(gap.from),
                  to: formatDay(gap.to),
                  have: gap.have,
                  need: Math.round((campaign.perWeek * (gap.to - gap.from + 1)) / DAYS_PER_WEEK),
                })}
              </span>
              {canManage && !campaign.creationPaused && (
                <button
                  className="button small"
                  onClick={() => {
                    commands.requestIdeas(campaign.id, actor);
                    notify(translate('AMY is suggesting more ideas.'));
                  }}
                >
                  {translate('Fill with ideas')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="panel">
        <div className="panel-header">
          <h2>
            <SafeIcon name="FiZap" size={18} /> {translate('Ideas')}
          </h2>
          {!closed && (
            <button
              className="button small"
              onClick={() => {
                commands.requestIdeas(campaign.id, actor);
                notify(translate('AMY is suggesting more ideas.'));
              }}
              disabled={readOnly || thinking || campaign.creationPaused}
              title={
                campaign.creationPaused ? translate('Resume content creation first') : undefined
              }
            >
              <SafeIcon name={thinking ? 'FiLoader' : 'FiPlus'} size={14} />
              {translate(thinking ? 'AMY is thinking…' : 'Ask for more ideas')}
            </button>
          )}
        </div>
        {thinking && (
          <div className="idea-skeleton" aria-live="polite">
            <span />
            <span />
            <span />
            <p className="muted small-print">
              {translate('AMY is reading your brief and brand guide v{v}…', { v: brand.version })}
            </p>
          </div>
        )}
        {suggested.length === 0 && !thinking && (
          <p className="muted">
            {translate(
              campaign.ideas.length
                ? 'No new ideas waiting. Ask AMY for more when you need them.'
                : 'No ideas yet. Ask AMY for the first ones.'
            )}
          </p>
        )}
        <div className="idea-grid">
          {suggested.map((idea) => (
            <article key={idea.id} className="idea-card">
              <div className="idea-meta">
                <SafeIcon name={channelIconName[idea.channel] as 'FiInstagram'} size={13} />
                {idea.channel} · {translate(idea.format)}
              </div>
              <h3>{idea.title}</h3>
              <p className="muted">{idea.angle}</p>
              <div className="button-row">
                {campaign.channels.includes(idea.channel) ? (
                  <button
                    className="button small primary"
                    onClick={() => accept(idea.id)}
                    disabled={!canManage || campaign.creationPaused || closed}
                  >
                    <SafeIcon name="FiCheck" size={14} />
                    {translate('Add to calendar')}
                  </button>
                ) : (
                  <span className="field-warn">
                    {translate('{channel} is no longer in this campaign.', {
                      channel: idea.channel,
                    })}
                  </span>
                )}
                <button
                  className="button small ghost"
                  onClick={() => commands.setIdeaStatus(campaign.id, idea.id, 'Declined')}
                  disabled={!canManage}
                >
                  {translate('Not this one')}
                </button>
              </div>
            </article>
          ))}
        </div>
        {inCalendar.length > 0 && (
          <div className="idea-done">
            <h3>{translate('In the calendar')}</h3>
            {inCalendar.map((idea) => {
              const post = posts.find((candidate) => candidate.id === idea.postId);
              return (
                <button
                  key={idea.id}
                  className="checklist-item list-button"
                  onClick={() => post && navigate({ page: 'post', id: post.id })}
                >
                  <SafeIcon name="FiCalendar" />
                  <span style={{ flex: 1 }}>
                    <strong>{idea.title}</strong> · {idea.channel}
                  </span>
                  {post && (
                    <span className={`status ${postStatusClass[post.status]}`}>
                      {translate(post.status)}
                    </span>
                  )}
                  {post && <span className="muted">{formatDay(post.day)}</span>}
                </button>
              );
            })}
          </div>
        )}
        {declined.length > 0 && (
          <div className="idea-done">
            <button
              className="link-button"
              onClick={() => setShowDeclined(!showDeclined)}
              aria-expanded={showDeclined}
            >
              {translate(showDeclined ? 'Hide declined ideas' : 'Show declined ideas ({n})', {
                n: declined.length,
              })}
            </button>
            {showDeclined &&
              declined.map((idea) => (
                <div key={idea.id} className="checklist-item">
                  <SafeIcon name="FiXCircle" />
                  <span style={{ flex: 1 }}>
                    {idea.title} · {idea.channel}
                  </span>
                  <button
                    className="button small ghost"
                    onClick={() => commands.setIdeaStatus(campaign.id, idea.id, 'Suggested')}
                    disabled={!canManage}
                  >
                    {translate('Bring back')}
                  </button>
                </div>
              ))}
          </div>
        )}
      </div>
      <div className="panel">
        <div className="panel-header">
          <h2>
            <SafeIcon name="FiCalendar" size={18} /> {translate('Posts')}
          </h2>
          <span className="muted">{formatCount(campaignPosts.length, 'post')}</span>
        </div>
        {campaignPosts.length === 0 && (
          <p className="muted">
            {translate('No posts yet. Accepted ideas appear here and in the calendar.')}
          </p>
        )}
        {campaignPosts.map((post) => (
          <button
            key={post.id}
            className="checklist-item list-button campaign-post"
            onClick={() => navigate({ page: 'post', id: post.id })}
          >
            {post.image ? <img src={post.image} alt="" /> : <SafeIcon name="FiImage" />}
            <span style={{ flex: 1, minWidth: 0 }}>
              <strong>{post.title}</strong>
              <span className="muted">
                {' '}
                · {post.channel} · {translate(post.format)}
              </span>
            </span>
            <span className={`status ${postStatusClass[post.status]}`}>
              {translate(post.status)}
            </span>
            <span className="muted campaign-post-day">
              {formatDay(post.day)} {post.time}
            </span>
          </button>
        ))}
      </div>
      <div className="panel">
        <h2>
          <SafeIcon name="FiFileText" size={18} /> {translate('Brief')}
        </h2>
        <p className="campaign-brief">{campaign.brief}</p>
        <dl className="asset-facts">
          {campaign.audience && (
            <div>
              <dt>{translate('Audience')}</dt>
              <dd>{campaign.audience}</dd>
            </div>
          )}
          {campaign.focus && (
            <div>
              <dt>{translate('Product focus')}</dt>
              <dd>{campaign.focus}</dd>
            </div>
          )}
          {campaign.languages?.length ? (
            <div>
              <dt>{translate('Content languages')}</dt>
              <dd>
                {campaign.languages
                  .map((languageCode) =>
                    languageCode === 'pl' ? translate('Polish') : translate('English')
                  )
                  .join(', ')}
              </dd>
            </div>
          ) : null}
          {accountNames.length > 0 && (
            <div>
              <dt>{translate('Accounts')}</dt>
              <dd>
                {accountNames.map((account) => `${account!.name} (${account!.network})`).join(', ')}
              </dd>
            </div>
          )}
        </dl>
        {campaign.materials.length > 0 && (
          <>
            <h3>{translate('Materials for AMY')}</h3>
            <div className="chat-attachments">
              {campaign.materials.map((name) => (
                <span key={name} className="attachment-chip">
                  <SafeIcon name="FiImage" size={12} />
                  {name}
                </span>
              ))}
            </div>
          </>
        )}
        <p className="muted small-print">
          {translate('AMY writes with brand guide v{v}. Created by {name}.', {
            v: brand.version,
            name: campaign.createdBy,
          })}
        </p>
        {role === 'agency' && (
          <button
            className="button small"
            onClick={() => {
              commands.saveTemplate(campaign.id);
              notify(translate('Saved as a template. It appears in New campaign for this agency.'));
            }}
          >
            <SafeIcon name="FiCopy" size={14} />
            {translate('Save as template')}
          </button>
        )}
      </div>

      {myTasks.length > 0 && (
        <div className="panel">
          <h2>
            <SafeIcon name="FiCheckSquare" size={18} /> {translate('AMY tasks for this campaign')}
          </h2>
          <TaskList tasks={myTasks} compact />
        </div>
      )}

      {!closed && canManage && campaign.status !== 'Draft' && (
        <div className="panel campaign-controls">
          <h2>
            <SafeIcon name="FiSliders" size={18} /> {translate('Controls')}
          </h2>
          <div className="control-row">
            <div>
              <strong>{translate('Content creation')}</strong>
              <p className="muted small-print">
                {translate(
                  'New ideas, drafts and automatic batches. Pausing does not touch scheduled posts.'
                )}
              </p>
            </div>
            <button
              className={`button small ${campaign.creationPaused ? 'primary' : ''}`}
              onClick={() => pause('creation', !campaign.creationPaused)}
            >
              <SafeIcon name={campaign.creationPaused ? 'FiPlay' : 'FiPause'} size={14} />
              {translate(campaign.creationPaused ? 'Resume creation' : 'Pause creation')}
            </button>
          </div>
          <div className="control-row">
            <div>
              <strong>{translate('Publishing')}</strong>
              <p className="muted small-print">
                {translate(
                  '{n} scheduled posts. Pausing holds them; nothing is published until you resume.',
                  { n: counts.scheduled }
                )}
              </p>
            </div>
            <button
              className={`button small ${campaign.publishingPaused ? 'primary' : ''}`}
              onClick={() => pause('publishing', !campaign.publishingPaused)}
            >
              <SafeIcon name={campaign.publishingPaused ? 'FiPlay' : 'FiPause'} size={14} />
              {translate(campaign.publishingPaused ? 'Resume publishing' : 'Pause publishing')}
            </button>
          </div>
          <div className="button-row campaign-danger">
            <button className="button danger-outline" onClick={() => setEnding(true)}>
              <SafeIcon name="FiFlag" />
              {translate('End campaign')}
            </button>
          </div>
        </div>
      )}

      {ending && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setEnding(false);
          }}
        >
          <section
            className="panel modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="end-title"
          >
            <h2 id="end-title">{translate('End “{name}”?', { name: campaign.name })}</h2>
            <ul className="brand-list">
              <li>
                {translate(
                  '{n} posts that are not approved yet will be cancelled and their slots freed.',
                  {
                    n: cancellable,
                  }
                )}
              </li>
              <li>
                {translate('{n} scheduled posts keep their schedule unless you stop them below.', {
                  n: scheduledLater,
                })}
              </li>
              <li>{translate('Published posts and their results stay.')}</li>
            </ul>
            {scheduledLater > 0 && (
              <label className="brand-check">
                <input
                  type="checkbox"
                  checked={stopScheduled}
                  onChange={(event) => setStopScheduled(event.target.checked)}
                />{' '}
                {translate('Also stop the {n} scheduled posts', { n: scheduledLater })}
              </label>
            )}
            <div className="button-row" style={{ marginTop: 14 }}>
              <button
                className="button danger"
                onClick={() => {
                  commands.endCampaign(campaign.id, stopScheduled, actor);
                  setEnding(false);
                  notify(translate('Campaign ended'));
                }}
              >
                {translate('End campaign')}
              </button>
              <button className="button" onClick={() => setEnding(false)}>
                {translate('Cancel')}
              </button>
            </div>
          </section>
        </div>
      )}
      {editing && (
        <CampaignWizard
          campaign={campaign}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            notify(translate('Campaign updated'));
          }}
        />
      )}
      {ideaOpen && (
        <AddIdeaSheet defaultCampaign={campaign.id} onClose={() => setIdeaOpen(false)} />
      )}
    </div>
  );
}
